import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import styled from "styled-components";
import ControlBar from "../components/TemplateSelection/ControlBar";
import AssetList from "../components/AssetList";
import { actions as assetsActions } from "../actions/assets";
import LuciPropTypes from "../propTypes/";
import { actions as assetActions } from "../actions/asset";
import Pagination from "../components/Pagination";
import { Container, StickyHeader } from "../styled-components/Layout";
import SelectedContentSidebar from "../components/ContentSelection/SelectedContentSidebar";
import AssetTypes from "../constants/assetTypes";
import SideBar from "../components/SideBar/SideBar";
import GraphicTemplatesSideBarContent from "../components/TemplateSelection/GraphicTemplatesSideBarContent";

const PaginationOuterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-content: flex-end;
`;

const PaginationInnerContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 11.5rem;
  flex-direction: column;
`;

const TemplateSelectionContainer = styled(Container)`
  z-index: 10000;
  background: ${props => props.theme.background};
`;

const RowContainer = styled(Container)`
  flex-direction: row;
  width: 100%;
`;

const SelectedContentContainer = styled.div`
  padding: 1rem;
  border-left: 1px solid ${props => props.theme.border};
  height: 100%;
`;

const AssetBrowserContainer = styled(Container)`
  width: 100%;
`;

/**
 * Main layout for content selection.
 */
class TemplateSelectionLayout extends React.PureComponent {
  UNSAFE_componentWillUpdate(nextProps) {
    const userLoggedIn = nextProps.authenticated;
    const userChanged = this.props.userId !== nextProps.userId;
    const categoriesPopulated = nextProps.categoriesInitialized;
    const contextsPopulated = nextProps.contextsInitialized;
    const filtersPopulated = nextProps.filtersInitialized;
    const optionsInitialized =
      contextsPopulated && categoriesPopulated && filtersPopulated;
    const assetsNeedUpdate = nextProps.needUpdate;
    const assetsLoading = nextProps.assetsLoading;
    const assetTypeChanged = this.props.assetType !== nextProps.assetType;
    const becameVisible = !this.props.visible && nextProps.visible;

    if (
      !nextProps.contextsLoading &&
      userChanged &&
      userLoggedIn &&
      !contextsPopulated
    ) {
      this.props.getContexts(nextProps.userId);
    }

    if (
      !nextProps.categoriesLoading &&
      ((userChanged && userLoggedIn) ||
        !categoriesPopulated ||
        assetTypeChanged)
    ) {
      this.props.getCategories(nextProps.assetType || "template");
    }

    if (
      !nextProps.filtersLoading &&
      ((userChanged && userLoggedIn) || assetTypeChanged || !filtersPopulated)
    ) {
      this.props.getFilterOptions(nextProps.userId, nextProps.assetType);
    }

    if (
      (becameVisible || assetsNeedUpdate) &&
      userLoggedIn &&
      optionsInitialized &&
      !assetsLoading
    ) {
      if (
        nextProps.changeTemplate &&
        nextProps.assetType != AssetTypes.GRAPHIC_TEMPLATE
      ) {
        nextProps.changeAssetType(AssetTypes.GRAPHIC_TEMPLATE);
      }
      this.props.getAssets(
        nextProps.changeTemplate ? "" : nextProps.assetType,
        !nextProps.changeTemplate
      );
    }
  }

  bindShortcuts = () => {
    if (this.controlBar) {
      this.controlBar.getWrappedInstance().bindShortcuts();
    }
  };

  unbindShortcuts = () => {
    if (this.controlBar) {
      this.controlBar.getWrappedInstance().unbindShortcuts();
    }
  };

  render() {
    const {
      assetList,
      selectedAssetId,
      toggleFavorite,
      loadAsset,
      changePage,
      assetsLoading,
      pageNumber,
      reload,
      error,
      totalPages,
      shortcuts,
      selectedContent,
      changeTemplate,
      swapTemplate,
      onConfirmAssetSelected
    } = this.props;

    return (
      <TemplateSelectionContainer>
        <RowContainer>
          <AssetBrowserContainer>
            <StickyHeader>
              <ControlBar ref={r => (this.controlBar = r)} />
            </StickyHeader>
            <RowContainer>
              <SideBar left>
                <GraphicTemplatesSideBarContent showTemplatesOnly />
              </SideBar>
              <AssetList
                assetList={assetList}
                selectedAssetId={selectedAssetId}
                toggleFavorite={toggleFavorite}
                onDoubleClick={res => {
                  if (changeTemplate) {
                    swapTemplate(res.id);
                  } else {
                    loadAsset(res.id, false, selectedContent);
                  }
                  onConfirmAssetSelected();
                }}
                loading={assetsLoading}
                reload={reload}
                error={error}
              />
              <PaginationOuterContainer>
                <PaginationInnerContainer>
                  <Pagination
                    nextPageShortcut={shortcuts.nextPage}
                    prevPageShortcut={shortcuts.prevPage}
                    totalPages={totalPages}
                    currentPage={pageNumber}
                    buffer={2}
                    onChange={changePage}
                  />
                </PaginationInnerContainer>
              </PaginationOuterContainer>
            </RowContainer>
          </AssetBrowserContainer>
          <Container>
            {selectedContent ? (
              <SelectedContentContainer>
                <SelectedContentSidebar contentItem={selectedContent} />
              </SelectedContentContainer>
            ) : (
              ""
            )}
          </Container>
        </RowContainer>
      </TemplateSelectionContainer>
    );
  }
}

TemplateSelectionLayout.defaultProps = {
  onConfirmAssetSelected: () => {},
  selectedAssetId: null,
  selectedContent: null,
  changeTemplate: false,
  visible: true
};

TemplateSelectionLayout.propTypes = {
  assetList: LuciPropTypes.AssetList.isRequired,
  selectedAssetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  toggleFavorite: PropTypes.func.isRequired,
  loadAsset: PropTypes.func.isRequired,
  getAssets: PropTypes.func.isRequired,
  assetsLoading: PropTypes.bool.isRequired,
  changePage: PropTypes.func.isRequired,
  pageNumber: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  reload: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired,
  userId: PropTypes.number.isRequired,
  getContexts: PropTypes.func.isRequired,
  getCategories: PropTypes.func.isRequired,
  assetType: PropTypes.string.isRequired,
  getFilterOptions: PropTypes.func.isRequired,
  // eslint-disable-next-line
  authenticated: PropTypes.bool.isRequired,
  shortcuts: PropTypes.shape({
    nextPage: PropTypes.arrayOf(PropTypes.string),
    prevPage: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  selectedContent: LuciPropTypes.ContentItem,
  changeTemplate: PropTypes.bool,
  swapTemplate: PropTypes.func.isRequired,
  onConfirmAssetSelected: PropTypes.func,
  changeAssetType: PropTypes.func.isRequired,
  visible: PropTypes.bool
};

const mapStateToProps = state => ({
  authenticated: state.auth.authenticated,
  assetList: state.assets.assetList,
  userId: state.auth.user.userId,
  assetsLoading: state.assets.assetsLoading,
  needUpdate: state.assets.needUpdate,
  contextList: state.assets.contextList,
  categoryList: state.assets.categoryList,
  categoriesInitialized: state.assets.categoriesInitialized,
  filtersInitialized: state.assets.filtersInitialized,
  contextsInitialized: state.assets.contextsInitialized,
  selectedAssetId: state.asset.selectedAssetId,
  pageNumber: state.assets.pageNumber,
  totalPages: state.assets.totalPages,
  error: state.assets.error,
  assetType: state.assets.assetType,
  shortcuts: state.keyShortcuts.shortcuts,
  filterList: state.assets.filterList,
  categoriesLoading: state.assets.categoriesLoading,
  contextsLoading: state.assets.contextsLoading,
  filtersLoading: state.assets.filtersLoading
});

const mapActionCreators = {
  toggleFavorite: assetsActions.toggleFavorite,
  getContexts: assetsActions.getContexts,
  loadAsset: assetActions.loadAsset,
  getAssets: assetsActions.getAssets,
  reload: assetsActions.reload,
  changePage: assetsActions.changePage,
  getFilterOptions: assetsActions.getFilterOptions,
  getCategories: assetsActions.getCategories,
  swapTemplate: assetActions.swapTemplate,
  changeAssetType: assetsActions.changeAssetType
};

export default connect(mapStateToProps, mapActionCreators, null, {
  withRef: true
})(TemplateSelectionLayout);



// WEBPACK FOOTER //
// ./app/layouts/TemplateSelectionLayout.js